import React, { useState, useEffect } from 'react';
import { MdClose, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useAuthStore } from '../store/authStore';
import { useAuthModalStore } from '../store/authModalStore';
import logoVin from '../assets/logoVin.jpg';
import '../styles/AuthModal.css';

/* ── Register Form ── */
const RegisterForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const register = useAuthStore((s) => s.register);
  const { switchView, close } = useAuthModalStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 12) {
      // Backend (POST /api/v1/users) enforces a 12-character minimum.
      setError('Password must be at least 12 characters');
      return;
    }
    setLoading(true);
    try {
      const [firstName, ...rest] = name.trim().split(' ');
      await register(email, password, firstName, rest.join(' ') || '');
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 className="auth-modal__title auth-modal__title--green">Register</h2>
      <p className="auth-modal__subtitle">JOIN TO US</p>

      {error && <div className="auth-modal__error">{error}</div>}

      <form className="auth-modal__form" onSubmit={handleSubmit}>
        <div className="auth-modal__field">
          <label className="auth-modal__label">Your name</label>
          <input className="auth-modal__input" type="text" placeholder="Jhon Deo" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="auth-modal__field">
          <label className="auth-modal__label">Email Address</label>
          <input className="auth-modal__input" type="email" placeholder="Example@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="auth-modal__field">
          <label className="auth-modal__label">Password</label>
          <div className="auth-modal__input-wrap">
            <input className="auth-modal__input" type={showPw ? 'text' : 'password'} placeholder="......" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button type="button" className="auth-modal__toggle" onClick={() => setShowPw(!showPw)}>{showPw ? <MdVisibility /> : <MdVisibilityOff />}</button>
          </div>
        </div>
        <div className="auth-modal__field">
          <label className="auth-modal__label">Confirm Password</label>
          <div className="auth-modal__input-wrap">
            <input className="auth-modal__input" type={showCpw ? 'text' : 'password'} placeholder="......" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            <button type="button" className="auth-modal__toggle" onClick={() => setShowCpw(!showCpw)}>{showCpw ? <MdVisibility /> : <MdVisibilityOff />}</button>
          </div>
        </div>
        <button type="submit" className="auth-modal__submit auth-modal__submit--green" disabled={loading}>
          {loading ? 'REGISTERING...' : 'REGISTER'}
        </button>
      </form>
      <p className="auth-modal__footer-text">
        ALREADY USER ? <button className="auth-modal__switch" onClick={() => switchView('login')}>LOGIN</button>
      </p>
    </>
  );
};

/* ── Login Form ── */
const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const login = useAuthStore((s) => s.login);
  const { switchView, close } = useAuthModalStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 className="auth-modal__title auth-modal__title--blue">Login</h2>
      <p className="auth-modal__subtitle">WELCOME BACK</p>

      {error && <div className="auth-modal__error">{error}</div>}

      <form className="auth-modal__form" onSubmit={handleSubmit}>
        <div className="auth-modal__field">
          <label className="auth-modal__label">Email Address</label>
          <input className="auth-modal__input" type="email" placeholder="Example@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="auth-modal__field">
          <label className="auth-modal__label">Password</label>
          <div className="auth-modal__input-wrap">
            <input className="auth-modal__input" type={showPw ? 'text' : 'password'} placeholder="......" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button type="button" className="auth-modal__toggle" onClick={() => setShowPw(!showPw)}>{showPw ? <MdVisibility /> : <MdVisibilityOff />}</button>
          </div>
        </div>
        <button type="submit" className="auth-modal__submit auth-modal__submit--blue" disabled={loading}>
          {loading ? 'LOGGING IN...' : 'LOGIN'}
        </button>
      </form>
      <p className="auth-modal__footer-text">
        NEW USER ? <button className="auth-modal__switch" onClick={() => switchView('register')}>REGISTER</button>
      </p>
    </>
  );
};

/* ── Modal Wrapper ── */
const AuthModal: React.FC = () => {
  const { isOpen, view, close } = useAuthModalStore();

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div className="auth-modal__overlay" onClick={close}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal__close" onClick={close} aria-label="Close">
          <MdClose />
        </button>

        <div className="auth-modal__layout">
          {/* Left: Illustration */}
          <div className="auth-modal__left">
            <div className="auth-modal__illustration">
              <div className="auth-modal__phone">
                <div className="auth-modal__phone-screen" />
              </div>
              <div className="auth-modal__icon auth-modal__icon--lock">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/></svg>
              </div>
              <div className="auth-modal__icon auth-modal__icon--shield">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>
              </div>
              <div className="auth-modal__icon auth-modal__icon--gear">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/></svg>
              </div>
              <div className="auth-modal__icon auth-modal__icon--dollar">$</div>
              <div className="auth-modal__icon auth-modal__icon--star">★</div>
              <div className="auth-modal__card" />
              <div className="auth-modal__person auth-modal__person--l" />
              <div className="auth-modal__person auth-modal__person--r" />
            </div>
          </div>

          {/* Right: Form */}
          <div className="auth-modal__right">
            <img src={logoVin} alt="VIN Wholesale" className="auth-modal__brand-logo" />
            {view === 'register' ? <RegisterForm /> : <LoginForm />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
